import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useCreateDeployment } from '../../../hooks/useCreateDeployment';
import { useServiceDesks } from '../../../hooks/useServiceDesks';
import { useServiceDeskCampaigns, type CampaignOption } from '../../../hooks/useServiceDeskCampaigns';
import { type CampaignType } from '../../../api/deploymentsApi';
import type { ServiceDeskOption } from '../../../api/serviceDesksApi';

interface DeploymentCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export const DeploymentCreateDialog = ({ open, onClose, onCreated }: DeploymentCreateDialogProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [serviceDeskId, setServiceDeskId] = useState<string>('');
  const [campaignType, setCampaignType] = useState<string>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const { serviceDesks: allServiceDesks } = useServiceDesks({ enabled: open });
  // Only service desks that have a `campaigns` config can host a campaign.
  const serviceDesks = useMemo(
    () => allServiceDesks.filter((sd: ServiceDeskOption) => sd.hasCampaigns),
    [allServiceDesks]
  );

  // Campaign types are fetched for the currently selected SD only.
  const selectedSdId = serviceDeskId ? Number(serviceDeskId) : null;
  const { options: campaignOptions, isLoading: campaignsLoading } =
    useServiceDeskCampaigns(selectedSdId);

  // Clear the campaign type whenever the SD changes (it may not exist on the new one).
  useEffect(() => {
    setCampaignType('');
  }, [serviceDeskId]);

  const { mutate: createDeployment, isPending, error: mutationError } = useCreateDeployment({
    onSuccess: (deployment) => {
      onCreated(deployment.id);
      setName('');
      setStartDate('');
      setEndDate('');
      setValidationError(null);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!name.trim() || !serviceDeskId || !campaignType || !startDate) {
      setValidationError(t('Name, service desk, campaign type and start date are required'));
      return;
    }

    createDeployment({
      name: name.trim(),
      service_desk_id: parseInt(serviceDeskId, 10),
      campaign_type: campaignType as CampaignType,
      start_date: startDate,
      end_date: endDate || null,
    });
  };

  const displayError = validationError ?? (mutationError as Error | null)?.message ?? null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('New campaign')}</DialogTitle>
          <DialogDescription>{t('Create a campaign in draft status')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">{t('Name *')}</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('Ex: Lancôme launch May 2026')}
            />
          </div>

          <div>
            <Label htmlFor="sd">{t('Service Desk *')}</Label>
            <Select value={serviceDeskId} onValueChange={setServiceDeskId}>
              <SelectTrigger id="sd">
                <SelectValue placeholder={t('Select a service desk')} />
              </SelectTrigger>
              <SelectContent>
                {serviceDesks.map((sd: ServiceDeskOption) => (
                  <SelectItem key={sd.id} value={sd.id.toString()}>
                    {sd.name} (ID: {sd.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="campaign-type">{t('Type of campaign *')}</Label>
            <Select
              value={campaignType}
              onValueChange={setCampaignType}
              disabled={!serviceDeskId || campaignsLoading || campaignOptions.length === 0}
            >
              <SelectTrigger id="campaign-type">
                <SelectValue
                  placeholder={
                    !serviceDeskId
                      ? t('Select a service desk first…')
                      : campaignsLoading
                      ? t('Loading…')
                      : campaignOptions.length === 0
                      ? t('No type available')
                      : t('Select a type')
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {campaignOptions.map((opt: CampaignOption) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start">{t('Start date *')}</Label>
              <Input id="start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="end">{t('End date')}</Label>
              <Input id="end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          {displayError && <p className="text-sm text-red-600">{displayError}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? t('Creating…') : t('Create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
